import { FormikHelpers } from 'formik';
import { useEffect, useRef, useState } from 'react';
import useRequest from 'src/core/api/useRequest';
import { closeCampaign, publishCampaign, createOrUpdateCampaign } from 'src/core/api/apiRequests';

export const useCampaignSubmit = (_isEditPage: boolean, _tableSettings: any) => {
  const [mainResult, requestMain, , mainLoading, mainStatus] = useRequest({ logLevel: ['error', 'success'] });
  const [closeCampaignResult, requestClose, , closeLoading, closeStatus] = useRequest({ logLevel: ['error', 'success'] });
  const [publishCampaignResult, requestPublish, , publishLoading, publishStatus] = useRequest({ logLevel: ['error', 'success'] });
  const [pending, setPending] = useState(false);
  const operationRef = useRef<{ kind: string; helpers: FormikHelpers<any>; started: boolean } | null>(null);
  useEffect(() => {
    const operation = operationRef.current;
    if (!operation) return;
    const status = operation.kind === 'main' ? mainStatus : operation.kind === 'close' ? closeStatus : publishStatus;
    if (status === 'loading') operation.started = true;
    if (!operation.started || status === 'loading' || status === 'idle') return;
    operation.helpers.setSubmitting(false);
    operationRef.current = null;
    setPending(false);
  }, [mainStatus, closeStatus, publishStatus]);
  const schedule = (kind: string, request: any, helpers: FormikHelpers<any>) => {
    if (operationRef.current) return;
    operationRef.current = { kind, helpers, started: false };
    setPending(true);
    (kind === 'main' ? requestMain : kind === 'close' ? requestClose : requestPublish)(request);
  };
  const submit = (values: any, campaignTableData: CampaignTableData, helpers: FormikHelpers<any>) => schedule('main', buildCampaignRequest({ values, campaignTableData }), helpers);
  const close = (campaignId: string, helpers: FormikHelpers<any>) => schedule('close', closeCampaign(campaignId), helpers);
  const publish = (campaignId: string, helpers: FormikHelpers<any>) => schedule('publish', publishCampaign(campaignId), helpers);
  return [{ mainResult, closeCampaignResult, publishCampaignResult }, submit, close, publish, pending || mainLoading || closeLoading || publishLoading] as const;
};

type CampaignDiscountEditType = 'amount' | 'percentage';
type CampaignPriceEditModes = Record<string, CampaignDiscountEditType>;
type CampaignTableData = { salesItems?: any[]; priceLists?: any[]; campaignPrices?: any[]; campaignPriceEditModes?: CampaignPriceEditModes };

const DISCOUNT_AMOUNT_TYPE = { Key: 2, Value: 'Discount Amount' };
const DISCOUNT_PERCENTAGE_TYPE = { Key: 3, Value: 'Discount Percentage' };

export const buildCampaignRequest = (payload: any) => {
  const values = payload?.values ?? payload;
  const campaignTableData = payload?.campaignTableData ?? {};
  const data = buildCampaignData(values, campaignTableData);
  return createOrUpdateCampaign(data);
};

export const buildCampaignData = (values: any, campaignTableData: CampaignTableData = {}) => {
  const campaignId = asId(values?.Id) ?? 0;
  const priceListRows = Array.isArray(campaignTableData.priceLists) ? campaignTableData.priceLists : values?.SalesPriceLists;
  const salesItemRows = Array.isArray(campaignTableData.salesItems) ? campaignTableData.salesItems : values?.CampaignSalesItems;
  const campaignPriceRows = Array.isArray(campaignTableData.campaignPrices) ? campaignTableData.campaignPrices : values?.CampaignPrices;
  const campaignPriceEditModes = campaignTableData.campaignPriceEditModes ?? {};
  return { Id: campaignId, Name: values?.Name ?? '', SalesText: values?.SalesText ?? '', CampaignTypeId: asId(values?.CampaignType) ?? asId(values?.CampaignTypeId) ?? null, ValidFrom: values?.ValidFrom ?? null, ValidTo: values?.ValidTo ?? null, SalesPriceLists: uniqueIds(priceListRows, getSalesPriceListId), CampaignSalesItems: uniqueIds(salesItemRows, getCampaignSalesItemId), CampaignPrices: asArray(campaignPriceRows).map((row: any) => buildCampaignPriceData(row, campaignId, campaignPriceEditModes)).filter((row: any) => row.SalesItemsId && row.SalesPriceListId) };
};

const buildCampaignPriceData = (row: any, campaignId: number, campaignPriceEditModes: CampaignPriceEditModes = {}) => {
  const originalPrice = asNumber(row?.OriginalPrice, 0);
  const campaignPrice = clampCampaignPrice(originalPrice, asNumber(row?.CampaignPrice, originalPrice));
  const discountEditType = getCampaignDiscountEditType(row, campaignPriceEditModes);
  const discountAmount = getDiscountAmountFromCampaignPrice(originalPrice, campaignPrice);
  const discountPercentage = getDiscountPercentageFromCampaignPrice(originalPrice, campaignPrice);
  const discountType = discountEditType === 'amount' ? DISCOUNT_AMOUNT_TYPE : DISCOUNT_PERCENTAGE_TYPE;
  return { CampaignId: asId(row?.CampaignId) ?? campaignId, SalesItemsId: getCampaignSalesItemId(row), SalesPriceListId: getCampaignPriceSalesPriceListId(row), DiscountType: discountType, DiscountValue: discountEditType === 'amount' ? discountAmount : discountPercentage, DiscountBasedOn: 0, Disabled: Boolean(row?.Disabled ?? false) };
};

const getCampaignDiscountEditType = (row: any, campaignPriceEditModes: CampaignPriceEditModes = {}): CampaignDiscountEditType => {
  const trackedEditType = campaignPriceEditModes[getCampaignPriceEditModeKey(row)];
  if (trackedEditType === 'amount' || trackedEditType === 'percentage') return trackedEditType;
  const raw = String(row?.CampaignDiscountEditType ?? row?.CampaignPriceEditType ?? row?.DiscountEditType ?? '').toLowerCase();
  if (raw === 'amount' || raw === 'value' || raw === 'fixed') return 'amount';
  if (raw === 'percentage' || raw === 'percent' || raw === 'rate') return 'percentage';
  const discountTypeId = asId(row?.DiscountType) ?? asId(row?.DiscountType_Key);
  return discountTypeId === 2 ? 'amount' : 'percentage';
};

const getDiscountAmountFromCampaignPrice = (originalPrice: number, campaignPrice: number) => {
  return round(Math.max(0, originalPrice - campaignPrice), 2);
};

const getDiscountPercentageFromCampaignPrice = (originalPrice: number, campaignPrice: number) => {
  if (!originalPrice || originalPrice <= 0) return 0;
  const safeCampaignPrice = clampCampaignPrice(originalPrice, campaignPrice);
  const discountPercentage = ((originalPrice - safeCampaignPrice) / originalPrice) * 100;
  return round(Math.max(0, Math.min(100, discountPercentage)), 2);
};

const clampCampaignPrice = (originalPrice: number, campaignPrice: number) => {
  if (!Number.isFinite(campaignPrice)) return originalPrice;
  if (campaignPrice < 0) return 0;
  if (originalPrice > 0 && campaignPrice > originalPrice) return originalPrice;
  return round(campaignPrice, 2);
};

const getCampaignPriceEditModeKey = (row: any) => String(row?.CampaignPriceRowId ?? `${getCampaignPriceSalesPriceListId(row) ?? 'no-price-list'}_${getCampaignSalesItemId(row) ?? 'no-sales-item'}`);
const getSalesPriceListId = (row: any) => asId(row?.SalesPriceList) ?? asId(row?.SalesPriceListId) ?? asId(row?.SalesPriceList_Key) ?? asId(row?.Id);
const getCampaignPriceSalesPriceListId = (row: any) => asId(row?.SalesPriceList) ?? asId(row?.SalesPriceListId) ?? asId(row?.SalesPriceList_Key);
const getCampaignSalesItemId = (row: any) => asId(row?.SalesItemsId) ?? asId(row?.SalesItemId) ?? asId(row?.SalesItem) ?? asId(row?.SalesItem_Key);
const primitiveValue = (value: any) => (value && typeof value === 'object' && ('value' in value || 'Value' in value) ? value.value ?? value.Value : value);
const keyOf = (value: any) => (value && typeof value === 'object' ? value.Key ?? value.key ?? value.value ?? value.Id ?? null : value);
const asId = (value: any) => {
  const n = Number(keyOf(value));
  return Number.isFinite(n) ? n : null;
};
const asNumber = (value: any, fallback = 0) => {
  const n = Number(String(primitiveValue(value) ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : fallback;
};
const asArray = (value: any) => (Array.isArray(value) ? value : []);
const uniqueIds = (rows: any, getId: (row: any) => number | null) => Array.from(new Set(asArray(rows).map(getId).filter((id): id is number => id !== null) ) );
const round = (value: number, dp = 2) => {
  const factor = Math.pow(10, dp);
  return Math.round(value * factor) / factor;
};

export default useCampaignSubmit;
